/**
 * GenericImageUpload Class
 * 
 * A comprehensive JavaScript class that handles image uploads and management for various 
 * components in the admin panel. It provides a unified interface for uploading images 
 * to temporary storage (via TmpImageService), managing image previews, and handling 
 * image deletion across different component types (slides, banners, products, artists, pages).
 * 
 * @fileoverview Generic image upload handler with Spatie MediaLibrary integration
 * @version 1.0.0
 * @since 2025
 * @author BackBeat Admin Panel
 * 
 * @example
 * // Basic usage
 * const uploader = new GenericImageUpload('my-uploader', {
 *     componentType: 'banner',
 *     componentId: '123',
 *     panelNum: 1,
 *     kind: 'panel_1',
 *     hiddenInput: '#panel_1_media_id'
 * });
 * 
 * @example
 * // Auto-initialization (recommended)
 * // HTML: <div id="my-uploader" class="image-uploader" data-component-type="banner" data-component-id="123" data-panel-num="1" data-kind="panel_1" data-hidden-input="#panel_1_media_id"></div>
 * // The class will automatically initialize on page load
 */
export default class GenericImageUpload {
    /**
     * Constructor
     * 
     * Initialize the GenericImageUpload instance with configuration options.
     * Sets up DOM references, configuration, and initializes the uploader.
     * 
     * @param {string} uploaderId - Unique identifier for the uploader DOM element
     * @param {Object} [options={}] - Configuration options
     * @param {string} [options.componentType='banner'] - Type of component ('banner', 'slide', 'product', 'artist', 'page')
     * @param {string} [options.componentId='new'] - ID of the component instance ('new' for new items)
     * @param {number} [options.panelNum=1] - Panel number for multi-panel components (1-5 for products, 1-3 for others)
     * @param {string} [options.uploadRoute='/api/admin/temp-images/upload'] - Custom upload endpoint (TmpImageController)
     * @param {string} [options.deleteRoute='/api/admin/temp-images/delete'] - Custom delete endpoint (TmpImageController)
     * @param {string|null} [options.kind=null] - Kind of image ('cover', 'background', 'panel_1', 'panel_2', 'panel_3', 'product_image_1', 'product_image_2', 'product_image_3', 'product_image_4', 'product_image_5', 'artist-image')
     * @param {string|null} [options.hiddenInput=null] - Selector for hidden input field to store media ID
     * 
     * @example
     * // Create uploader for banner panel 1
     * const uploader = new GenericImageUpload('banner-panel-1', {
     *     componentType: 'banner',
     *     componentId: '123',
     *     panelNum: 1,
     *     kind: 'panel_1',
     *     hiddenInput: '#panel_1_media_id'
     * });
     */
    constructor(uploaderId, options = {}) {
        this.uploaderId = uploaderId;
        this.componentType = options.componentType || 'banner';
        this.componentId = options.componentId || 'new';
        this.panelNum = options.panelNum || 1;
        this.uploadRoute = options.uploadRoute || '/api/admin/temp-images/upload';
        this.deleteRoute = options.deleteRoute || '/api/admin/temp-images/delete';
        this.kind = options.kind || null;
        this.hiddenInput = options.hiddenInput || null;

        this.$uploader = $('#' + this.uploaderId);
        this.init();
    }

    /**
     * Initialize the uploader
     * 
     * Sets up containers, binds events, and initializes current image state.
     * This method is called automatically by the constructor.
     * 
     * @private
     * @returns {void}
     */
    init() {
        this.setupContainers();
        this.bindEvents();
        this.initializeCurrentImage();
    }

    setupContainers() {
        // Get or create controls container
        let $controlsContainer = this.$uploader.next('.image-uploader-controls');
        if ($controlsContainer.length === 0) {
            $controlsContainer = $('<div class="image-uploader-controls"></div>');
            this.$uploader.after($controlsContainer);
        }

        this.$controlsContainer = $controlsContainer;
        this.$uploadBtn = $controlsContainer.find('.d-flex .btn-upload-image');
        this.$fileInput = $controlsContainer.find('.d-flex .file-input');
        this.$progress = $controlsContainer.find('.upload-progress');
        this.$progressBar = this.$progress.find('.progress-bar');
        this.$progressText = this.$progress.find('.progress-text');
        this.$messages = $controlsContainer.find('.upload-messages');
        // Read data-kind and data-hidden-input attributes
        const dataKind = this.$uploader.data('kind');
        const dataHidden = this.$uploader.data('hidden-input');
        if (dataKind) this.kind = dataKind;
        if (dataHidden) this.hiddenInput = dataHidden;
    }

    bindEvents() {
        // Upload button click
        this.$uploadBtn.on('click', () => {
            this.$fileInput.click();
        });

        // File input change
        this.$fileInput.on('change', (e) => {
            const file = e.target.files[0];
            if (file) {
                this.uploadImage(file);
            }
        });

        // Remove button click - use document delegation with specific selector
        $(document).on('click', '.btn-remove-image', (e) => {
            console.log('Remove button clicked - checking if for uploader:', this.uploaderId);

            // Check if this click is for THIS specific uploader by checking the closest uploader
            const $clickedButton = $(e.target).closest('.btn-remove-image');
            const $controlsContainer = $clickedButton.closest('.image-uploader-controls');
            const $associatedUploader = $controlsContainer.prev('.image-uploader');

            console.log('Clicked button:', $clickedButton.length);
            console.log('Controls container:', $controlsContainer.length);
            console.log('Associated uploader:', $associatedUploader.length);
            console.log('Associated uploader ID:', $associatedUploader.attr('id'));
            console.log('Expected uploader ID:', this.uploaderId);

            if ($associatedUploader.attr('id') === this.uploaderId) {
                console.log('Remove button clicked for uploader:', this.uploaderId);
                this.removeImage();
                return; // Stop execution for this widget
            } else {
                console.log('Remove button click ignored - not for this uploader');
            }
        });
    }

    initializeCurrentImage() {
        const $currentImage = this.$uploader.find('.uploaded-image');
        if ($currentImage.length > 0) {
            const currentImageUrl = $currentImage.attr('src');
            this.$uploader.data('image-url', currentImageUrl);
        }
    }

    /**
     * Upload an image file
     * 
     * Handles the complete upload process including validation, form data creation,
     * AJAX request, response handling, and UI updates. Supports both Spatie MediaLibrary
     * components (slides, banners) and legacy components (products, pages).
     * 
     * @param {File} file - The image file to upload
     * @returns {void}
     * 
     * @example
     * // Upload a file when user selects it
     * const fileInput = document.getElementById('file-input');
     * fileInput.addEventListener('change', (e) => {
     *     const file = e.target.files[0];
     *     if (file) {
     *         uploader.uploadImage(file);
     *     }
     * });
     */
    uploadImage(file) {
        console.log('=== UPLOAD START ===');
        console.log('Uploader ID:', this.uploaderId);
        console.log('Hidden Input:', this.hiddenInput);
        console.log('Kind:', this.kind);

        // Check current state of all media fields before upload
        const form = this.$uploader.closest('form');
        if (form.length > 0) {
            const mediaFields = form.find('input[name*="media_id"]');
            console.log('BEFORE UPLOAD - Media ID fields:', mediaFields.map((i, el) => ({ name: $(el).attr('name'), value: $(el).val() })).get());
        }

        const formData = new FormData();
        formData.append('image', file);
        formData.append('component_type', this.componentType);
        formData.append('component_id', this.componentId);
        formData.append('panel_num', this.panelNum);
        if (this.kind) {
            formData.append('kind', this.kind);
        }
        formData.append('_token', $('meta[name="csrf-token"]').attr('content'));

        this.$progress.show();
        this.$uploadBtn.prop('disabled', true);
        this.$messages.empty();

        $.ajax({
            url: this.uploadRoute,
            type: 'POST',
            data: formData,
            processData: false,
            contentType: false,
            xhr: () => {
                const xhr = new window.XMLHttpRequest();
                xhr.upload.addEventListener("progress", (evt) => {
                    if (evt.lengthComputable) {
                        const percentComplete = evt.loaded / evt.total * 100;
                        this.$progressBar.css('width', percentComplete + '%');
                        this.$progressText.text('Uploading... ' + Math.round(percentComplete) + '%');
                    }
                }, false);
                return xhr;
            },
            success: (response) => {
                this.$progress.hide();
                this.$uploadBtn.prop('disabled', false);
                this.$fileInput.val('');

                if (response.data && response.data.success) {
                    this.showMessage('success', response.data.message || 'Image uploaded successfully!');
                    this.updateImagePreview(response.data.image_url, response.data.filename);
                    this.$uploader.data('temp-image-filename', this.extractImageName(response.data.filename));
                    // Store dimensions if provided
                    if (response.data.width && response.data.height) {
                        this.showDimensions(response.data.width, response.data.height);
                    }
                    // Save media_id into a hidden field if specified
                    if (this.hiddenInput && response.data.media_id) {
                        console.log('Looking for hidden field:', this.hiddenInput, 'for uploader:', this.uploaderId);

                        // Find the specific hidden field for this uploader
                        let fieldName = this.hiddenInput.replace('#','');

                        // First try to find the field that's specifically associated with this uploader
                        let $hidden = this.$uploader.siblings(`input[name="${fieldName}"]`);
                        console.log('Found sibling by name:', $hidden.length, 'fieldName:', fieldName);

                        if ($hidden.length === 0) {
                            // Try to find anywhere in the form
                            $hidden = $(`input[name="${fieldName}"]`);
                            console.log('Found anywhere by name:', $hidden.length, 'fieldName:', fieldName);
                        }

                        if ($hidden.length === 0) {
                            // Try by ID as fallback
                            $hidden = $(this.hiddenInput);
                            console.log('Found by ID:', $hidden.length);
                            if ($hidden.length === 0) {
                                $hidden = $('<input>', { type: 'hidden', id: fieldName, name: fieldName });
                                this.$uploader.after($hidden);
                                console.log('Created new hidden field:', this.hiddenInput);
                            }
                        }

                        // Set the value
                        $hidden.val(response.data.media_id);
                        console.log('Setting hidden field', this.hiddenInput, 'to value', response.data.media_id);
                        console.log('Hidden field value after setting:', $hidden.val());
                        console.log('Hidden field is inside form:', $hidden.closest('form').length > 0);
                        console.log('Hidden field name:', $hidden.attr('name'));

                        // Debug: check all hidden fields in form
                        const form = $hidden.closest('form');
                        if (form.length > 0) {
                            const allHiddenFields = form.find('input[type="hidden"]');
                            console.log('All hidden fields in form:', allHiddenFields.map((i, el) => ({ name: $(el).attr('name'), value: $(el).val() })).get());

                            // Check specifically for media_id fields
                            const mediaFields = form.find('input[name*="media_id"]');
                            console.log('Media ID fields:', mediaFields.map((i, el) => ({ name: $(el).attr('name'), value: $(el).val() })).get());
                        }

                        // Debug: check if field will be submitted
                        setTimeout(() => {
                            console.log('Form will submit with field:', $hidden.attr('name'), '=', $hidden.val());

                            // Check final state of all media fields after upload
                            const form = $hidden.closest('form');
                            if (form.length > 0) {
                                const mediaFields = form.find('input[name*="media_id"]');
                                console.log('AFTER UPLOAD - Media ID fields:', mediaFields.map((i, el) => ({ name: $(el).attr('name'), value: $(el).val() })).get());

                                form.on('submit', function() {
                                    console.log('Form submitting with data:', $(this).serialize());
                                });
                            }
                        }, 100);
                    }
                } else {
                    this.showMessage('error', response.message || 'Upload failed!');
                }
            },
            error: (xhr) => {
                this.$progress.hide();
                this.$uploadBtn.prop('disabled', false);
                this.$fileInput.val('');

                let errorMessage = 'Upload failed!';
                if (xhr.responseJSON) {
                    if (xhr.responseJSON.message) {
                        errorMessage = xhr.responseJSON.message;
                    } else if (xhr.responseJSON.errors) {
                        errorMessage = Object.values(xhr.responseJSON.errors).flat().join(', ');
                    }
                }
                this.showMessage('error', errorMessage);
            }
        });
    }

    /**
     * Remove uploaded image
     * 
     * Handles image removal for both temporary uploads (Spatie MediaLibrary) and legacy components.
     * For Spatie components, deletes the media via TempImageController API.
     * For legacy components, removes the temporary file.
     * 
     * Components using Spatie MediaLibrary (from config/media.php spatie_components):
     * - slide, banner, artist, product, account-customization
     * 
     * @returns {void}
     */
    removeImage() {
        console.log('=== REMOVE IMAGE START ===');
        console.log('Uploader ID:', this.uploaderId);
        console.log('Hidden Input:', this.hiddenInput);
        console.log('Component Type:', this.componentType);

        // Components that use Spatie MediaLibrary (synced with config/media.php)
        const spatieComponents = ['slide', 'banner', 'artist', 'product', 'account-customization'];

        // Check if this component uses Spatie MediaLibrary
        if (spatieComponents.includes(this.componentType)) {
            console.log(`${this.componentType} component - deleting media from Spatie`);
            this.removeSpatieMedia();
            return;
        }

        // For legacy components (pages, etc.) use the original logic
        this.removeLegacyImage();
    }

    /**
     * Remove image for Spatie MediaLibrary components
     * 
     * @private
     */
    removeSpatieMedia() {
        // Get the media_id from hidden field
        let mediaId = this.getMediaIdFromHiddenField();
        
        // If we have a media_id, delete it from TempImageController
        if (mediaId && mediaId !== 'REMOVE') {
            console.log('Found media_id to delete:', mediaId);
            
            $.ajax({
                url: this.deleteRoute.replace('MEDIA_ID', mediaId),
                type: 'DELETE',
                data: {
                    _token: $('meta[name="csrf-token"]').attr('content')
                },
                success: (response) => {
                    console.log('Media deleted from Spatie:', response);
                    this.clearImagePreview();
                    this.showMessage('success', 'Image removed successfully!');
                },
                error: (xhr) => {
                    console.log('Failed to delete media from Spatie:', xhr);
                    let errorMessage = 'Failed to remove image!';
                    if (xhr.responseJSON && xhr.responseJSON.message) {
                        errorMessage = xhr.responseJSON.message;
                    }
                    this.showMessage('error', errorMessage);
                }
            });
        } else {
            // No media_id (existing image), just clear the preview and signal removal
            console.log('No media_id found - this is an existing image, will be removed on form submit');
            this.clearImagePreview();
            this.showMessage('success', 'Image will be removed when you save the form!');
        }
    }

    /**
     * Remove image for legacy components
     * 
     * @private
     */
    removeLegacyImage() {
        const filename = this.$uploader.data('temp-image-filename');
        console.log('Legacy component - filename:', filename);
        
        if (!filename) {
            // Just remove preview if no uploaded file
            this.$uploader.find('.image-preview').remove();
            this.$uploader.removeData('image-url');
            this.$uploader.removeClass('has-image');

            // Remove image actions
            this.$controlsContainer.find('.image-actions').remove();

            // Clear hidden field to signal image removal (for existing images)
            if (this.hiddenInput) {
                let $hidden = $(this.hiddenInput);
                if ($hidden.length === 0) {
                    // Try to find by name instead of id
                    let fieldName = this.hiddenInput.replace('#','');
                    $hidden = $(`input[name="${fieldName}"]`);
                }
                if ($hidden.length > 0) {
                    $hidden.val('');
                    console.log('Cleared hidden field', this.hiddenInput, 'due to existing image removal');
                }
            }

            return;
        }

        $.ajax({
            url: this.deleteRoute.replace('TEMP_ID', filename),
            type: 'DELETE',
            data: {
                _token: $('meta[name="csrf-token"]').attr('content')
            },
            success: (response) => {
                if (response.message) {
                    this.$uploader.find('.image-preview').remove();
                    this.$uploader.removeData('temp-image-filename');
                    this.$uploader.removeData('image-url');
                    this.$uploader.removeClass('has-image');

                    // Remove image actions
                    this.$controlsContainer.find('.image-actions').remove();

                    // Clear hidden field to signal image removal
                    if (this.hiddenInput) {
                        let $hidden = $(this.hiddenInput);
                        if ($hidden.length === 0) {
                            // Try to find by name instead of id
                            let fieldName = this.hiddenInput.replace('#','');
                            $hidden = $(`input[name="${fieldName}"]`);
                        }
                        if ($hidden.length > 0) {
                            $hidden.val('');
                            console.log('Cleared hidden field', this.hiddenInput, 'due to image deletion');
                        }
                    }

                    this.showMessage('success', response.message);
                }
            },
            error: (xhr) => {
                let errorMessage = 'Failed to remove image!';
                if (xhr.responseJSON && xhr.responseJSON.message) {
                    errorMessage = xhr.responseJSON.message;
                }
                this.showMessage('error', errorMessage);
            }
        });
    }

    /**
     * Get media ID from hidden field
     * 
     * @private
     * @returns {string|null} The media ID or null if not found
     */
    getMediaIdFromHiddenField() {
        if (!this.hiddenInput) {
            return null;
        }
        
        let $hidden = this.getHiddenField();
        return $hidden.length > 0 ? $hidden.val() : null;
    }

    /**
     * Get hidden field jQuery object
     * 
     * @private
     * @returns {jQuery} The hidden field jQuery object
     */
    getHiddenField() {
        let $hidden = $(this.hiddenInput);
        if ($hidden.length === 0) {
            // Try to find by name instead of id
            let fieldName = this.hiddenInput.replace('#', '');
            $hidden = $(`input[name="${fieldName}"]`);
        }
        return $hidden;
    }

    updateImagePreview(imageUrl, filename) {
        let $imagePreviewContainer = this.$uploader.find('.image-preview');
        if ($imagePreviewContainer.length === 0) {
            $imagePreviewContainer = $('<div class="image-preview"></div>');
            this.$uploader.prepend($imagePreviewContainer);
        }

        $imagePreviewContainer.html(`
            <img class="uploaded-image" src="${imageUrl}" alt="${this.componentType} Panel ${this.panelNum}">
        `);

        // Find or create the d-flex container
        let $dFlex = this.$controlsContainer.find('.d-flex');
        if ($dFlex.length === 0) {
            $dFlex = $('<div class="d-flex"></div>');
            this.$controlsContainer.prepend($dFlex);
        }

        // Add upload button to d-flex if not already there
        let $uploadButton = $dFlex.find('.upload-button');
        if ($uploadButton.length === 0) {
            $dFlex.append(`
                <div class="upload-button">
                    <button type="button" class="btn btn-success btn-upload-image">
                        <i class="fa fa-upload"></i> Upload Image
                    </button>
                    <input type="file" class="file-input" accept="image/*" style="display: none;">
                </div>
            `);
        }

        // Add remove button to d-flex
        let $imageActions = $dFlex.find('.image-actions');
        if ($imageActions.length === 0) {
            $imageActions = $('<div class="image-actions"></div>');
            $dFlex.append($imageActions);
        }

        $imageActions.html(`
            <button type="button" class="btn btn-danger btn-remove-image">
                <i class="fa fa-trash"></i> Remove
            </button>
        `);

        // Add has-image class to indicate an image is present
        this.$uploader.addClass('has-image');

        // Store the image URL in the uploader's data attributes
        this.$uploader.data('image-url', imageUrl);
    }

    showDimensions(width, height) {
        let $dims = this.$controlsContainer.find('.image-dimensions');
        if ($dims.length === 0) {
            $dims = $('<div class="image-dimensions text-muted" style="margin-top:6px"></div>');
            this.$controlsContainer.append($dims);
        }
        $dims.text(`(${width} x ${height})`);
    }

    showMessage(type, message) {
        const alertClass = type === 'success' ? 'alert-success' : 'alert-danger';
        const messageHtml = `
            <div class="alert ${alertClass} alert-dismissible" role="alert" style="background-color: ${type === 'success' ? '#d4edda' : '#f8d7da'} !important; border-color: ${type === 'success' ? '#c3e6cb' : '#f5c6cb'} !important; color: ${type === 'success' ? '#155724' : '#721c24'} !important;">
                <button type="button" class="close" data-dismiss="alert">
                    <span aria-hidden="true">&times;</span>
                </button>
                ${message}
            </div>
        `;

        const $messages = this.$controlsContainer.find('.upload-messages');
        if ($messages.length === 0) {
            this.$controlsContainer.append('<div class="upload-messages"></div>');
        }

        this.$controlsContainer.find('.upload-messages').html(messageHtml);

        setTimeout(() => {
            this.$controlsContainer.find('.upload-messages .alert').fadeOut();
        }, 5000);
    }

    // Public method to get current image URL
    getCurrentImageUrl() {
        return this.$uploader.data('image-url');
    }

    // Public method to get temp filename
    getTempFilename() {
        return this.$uploader.data('temp-image-filename');
    }
    extractImageName(imageUrl) {
        return imageUrl.split('/').pop();
    }

    clearImagePreview() {
        this.$uploader.find('.image-preview').remove();
        this.$uploader.removeData('temp-image-filename');
        this.$uploader.removeData('image-url');
        this.$uploader.removeClass('has-image');

        // Remove image actions
        this.$controlsContainer.find('.image-actions').remove();

        // Clear hidden field to signal image removal
        if (this.hiddenInput) {
            let $hidden = $(this.hiddenInput);
            if ($hidden.length === 0) {
                // Try to find by name instead of id
                let fieldName = this.hiddenInput.replace('#','');
                $hidden = $(`input[name="${fieldName}"]`);
            }
            if ($hidden.length > 0) {
                $hidden.val('REMOVE'); // Special value means "remove this image"
                $hidden.attr('value', 'REMOVE'); // Force special value attribute
                console.log('Set hidden field', this.hiddenInput, 'to REMOVE due to image removal');
                console.log('Hidden field value after setting:', $hidden.val());
                console.log('Hidden field value type:', typeof $hidden.val());
            }
        }
    }
}

// Auto-initialize all image uploaders on page load
$(document).ready(function() {
    $('.image-uploader').each(function() {
        const $uploader = $(this);
        const uploaderId = $uploader.attr('id');

        if (uploaderId && !$uploader.data('initialized')) {
            const options = {
                componentType: $uploader.data('component-type'),
                componentId: $uploader.data('component-id'),
                panelNum: $uploader.data('panel-num'),
                uploadRoute: $uploader.data('upload-route'),
                deleteRoute: $uploader.data('delete-route'),
                kind: $uploader.data('kind'),
                hiddenInput: $uploader.data('hidden-input')
            };

            const uploader = new GenericImageUpload(uploaderId, options);
            $uploader.data('uploader-instance', uploader);
            $uploader.data('initialized', true);
        }
    });
});
